import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { ConfirmProvider } from "@/components/Confirm";
import { TenantProvider } from "@/context/TenantContext";
import { SettingsProvider } from "@/context/SettingsContext";
import { AdminRoute, EditorRoute, OwnerRoute, ProtectedRoute, SignedInRoute } from "@/components/Bits";
import Login, { ResetPassword, SetPassword } from "@/pages/Auth";
import Overview from "@/pages/Overview";
import Catalog from "@/pages/Catalog";
import TrackEditor from "@/pages/TrackEditor";
import Releases from "@/pages/Releases";
import Production from "@/pages/Production";
import Registrations from "@/pages/Registrations";
import Writers from "@/pages/Writers";
import Import from "@/pages/Import";
import Settings from "@/pages/Settings";
import Team from "@/pages/Team";
import Workspaces from "@/pages/Workspaces";
import PlatformOverview, { PlatformPeople, PlatformSettings } from "@/pages/Platform";
import { useAuth } from "@/context/AuthContext";
import Profile from "@/pages/Profile";
import Support from "@/pages/Support";
import MyTickets from "@/pages/MyTickets";
import Royalties from "@/pages/Royalties";
import Payouts from "@/pages/Payouts";
import { MySongs, WriterDashboard } from "@/pages/Writer";
import RoyaltyImport from "@/pages/RoyaltyImport";
import Albums, { AlbumPage } from "@/pages/Albums";
import PageErrorBoundary from "@/components/PageErrorBoundary";
import InstallPrompt from "@/components/InstallPrompt";

/** Where "/" (or an unknown address) goes: owners to their console, everyone else to their workspace. */
function Home() {
  const { user, loading } = useAuth();
  if (loading || user === null) return null;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={user.role === "owner" ? "/platform" : user.role === "writer" ? "/dashboard" : "/overview"} replace />;
}

export default function App() {
  return (
    <BrowserRouter>
      <ConfirmProvider>
        <AuthProvider>
          <TenantProvider>
            <SettingsProvider>
              <PageErrorBoundary>
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route element={<SignedInRoute />}>
                  <Route path="/set-password" element={<SetPassword />} />
                </Route>

                <Route element={<ProtectedRoute />}>
                  <Route path="/overview" element={<Overview />} />
                  <Route path="/catalog" element={<Catalog />} />
                  <Route path="/releases" element={<Releases />} />
                  <Route path="/registrations" element={<Registrations />} />
                  <Route path="/writers" element={<Writers />} />
                  <Route path="/royalties" element={<Royalties />} />
                  <Route path="/payouts" element={<Payouts />} />
                  <Route path="/dashboard" element={<WriterDashboard />} />
                  <Route path="/my-songs" element={<MySongs />} />
                  <Route path="/profile" element={<Profile />} />
                  <Route path="/support" element={<MyTickets />} />
                </Route>

                <Route element={<EditorRoute />}>
                  <Route path="/catalog/new" element={<TrackEditor />} />
                  <Route path="/catalog/:id" element={<TrackEditor />} />
                  <Route path="/albums" element={<Albums />} />
                  <Route path="/albums/:id" element={<AlbumPage />} />
                  <Route path="/production" element={<Production />} />
                  <Route path="/import" element={<Import />} />
                  <Route path="/royalties/import" element={<RoyaltyImport />} />
                </Route>

                <Route element={<AdminRoute />}>
                  <Route path="/settings" element={<Settings />} />
                  <Route path="/team" element={<Team />} />
                </Route>

                <Route element={<OwnerRoute />}>
                  <Route path="/platform" element={<PlatformOverview />} />
                  <Route path="/platform/people" element={<PlatformPeople />} />
                  <Route path="/platform/settings" element={<PlatformSettings />} />
                  <Route path="/workspaces" element={<Workspaces />} />
                  <Route path="/support-inbox" element={<Support />} />
                </Route>

                <Route path="*" element={<Home />} />
              </Routes>
              </PageErrorBoundary>
              <Toaster position="top-right" richColors closeButton duration={2500} />
              <InstallPrompt />
            </SettingsProvider>
          </TenantProvider>
        </AuthProvider>
      </ConfirmProvider>
    </BrowserRouter>
  );
}
